import { DateTime } from "luxon"

import { ProductFields } from "@tenforce/product-fields"

import { dataAPI } from "./api"
import {
    parseOdataDateValue,
    getFormatBasedOnType,
} from "./utils/dateTimeParsing"
import {
    parseFieldData,
    parseOptionField,
    parseItemAction,
    parseLocationCoordinates,
} from "./utils/fieldparsing"
import {
    ConceptCode,
    DescriptionComponentCode,
    StatusCode,
} from "./utils/constants"
import { RequestErrorDataApi } from "../errors"
import env from "../env"
import { ITabComponent } from "../components/itemdetail"
import {
    BasicOptionDTO,
    FieldDTO,
    FlowControlDTO,
    ItemActionDTO,
    ItemViewComponentDTO,
    ItemViewGroupingComponentDTO,
    LocationFieldDTO,
    OptionItemDTO,
    SelectableValues,
    SelectableValuesGeneric,
    SingleFieldComponentDTO,
    MultiFieldsComponentDTO,
    TagDTO,
} from "./models/fields"
import { ODataGetArrayResponse } from "./models/api/data"
import {
    IField,
    IItemPickerField,
    ILocationField,
    ITreeSelectorField,
    IToggleButtonField,
    ITag,
} from "../components/fields"
import { IUserFormattingSettings } from "../components/applicationheader/components/usermenu"
import { IButton } from "../components/common/contextmenu"
import {
    parseItemPickerData,
    selectorToSingularTypeMapper,
} from "./treeSelector/utils"
import { selectorModelMapper } from "./treeSelector/utils"
import { TypeModel, ItemDTO } from "./models/treeSelector/models"
import { isFieldTypeSingleSelect } from "../components/fields/components/treeSelector/utils"
import { parseTagData } from "./tagPicker"
import { apiPath } from "./api/data"

type allField = {
    Value: unknown
    "Value@delta"?: never
}
type deltaField = {
    "Value@delta": unknown
    Value?: never
}

type FieldArgs = {
    "@type": string
} & (allField | deltaField)

export const getFields = (
    itemId: number,
    userFormattingSettings: IUserFormattingSettings,
): Promise<IField[]> =>
    dataAPI
        .getJSON<ItemViewGroupingComponentDTO>(
            `itemviews(itemid=${itemId},code='itemdetail')`,
        )
        .then((itemDetail) => {
            return extractFields(itemDetail, userFormattingSettings)
        })

export const updateFieldValue = async (
    itemId: number,
    field: IField,
    oldValues?: unknown,
): Promise<IField> => {
    const { fieldId, value } = field

    let args: FieldArgs = {
        "@type": field.odataType,
        Value: value,
    } as FieldArgs

    if (field.type === "date") {
        const newDate = field.value
            ? DateTime.fromISO(field.value as string)
            : null

        const format = getFormatBasedOnType(field.odataType)
        if (format === "ISO") {
            args.Value = parseOdataDateValue(field.value as string, format)
        } else {
            args.Value = newDate?.toFormat(format) ?? null
        }
    }

    if (field.type === "itemPicker") {
        const parsedField = field as IItemPickerField
        const parsedValue = value as number[]
        if (parsedField.isSingleSelect) {
            if (parsedValue.length) {
                args.Value = {
                    "@type":
                        selectorToSingularTypeMapper[parsedField.odataType],
                    id: parsedValue[0],
                }
            } else {
                args.Value = null
            }
        } else {
            args.Value = parsedValue.map((valueId) => ({
                "@type": selectorToSingularTypeMapper[parsedField.odataType],
                id: valueId,
            }))
        }
    }

    if (field.type === "location") {
        const value = (field as ILocationField).value
        args.Value = {
            Point: {
                type: "Point",
                coordinates: [value?.longitude, value?.latitude],
            },
        }
    }

    if (field.type === "time") {
        let newValue = null
        if (value) {
            newValue = DateTime.fromISO(value as string)
            const format = getFormatBasedOnType(field.odataType)
            if (format === "ISO") {
                newValue = parseOdataDateValue(field.value as string, format)
            } else {
                newValue = newValue?.toFormat(format) ?? null
            }
        }

        args.Value = newValue
    }

    if (field.type === "selector") {
        const parsedField = field as ITreeSelectorField
        const parsedValue = value as number[]
        const parsedOldValue =
            (oldValues as Array<{ id: string }> | null)?.map(
                (item) => item.id,
            ) || []

        if (parsedField.isSingleSelect) {
            if (parsedValue.length) {
                args.Value = {
                    "@type":
                        selectorToSingularTypeMapper[parsedField.odataType],
                    id: parsedValue[0],
                }
            } else {
                args.Value = null
            }
        } else {
            let deltaValue = []
            if (parsedOldValue && parsedOldValue.length) {
                const normalize = (val: string | number): string =>
                    val.toString()

                const oldNorm = parsedOldValue.map(normalize)
                const newNorm = parsedValue.map(normalize)

                const oldSet = new Set(oldNorm)
                const newSet = new Set(newNorm)

                const idsToAdd = newNorm.filter((id) => !oldSet.has(id))
                const idsToRemove = oldNorm.filter((id) => !newSet.has(id))

                deltaValue = [
                    ...idsToAdd.map((id: string) => ({
                        "@type":
                            selectorToSingularTypeMapper[parsedField.odataType],
                        id: id,
                    })),
                    ...idsToRemove.map((id: string) => ({
                        "@removed": {
                            reason: "changed",
                        },
                        "@type":
                            selectorToSingularTypeMapper[parsedField.odataType],
                        id: id,
                    })),
                ]
            } else {
                deltaValue = parsedValue.map((valueId) => ({
                    "@type":
                        selectorToSingularTypeMapper[parsedField.odataType],
                    id: valueId,
                }))
            }

            args = {
                "@type": field.odataType,
                "Value@delta": deltaValue,
            } as FieldArgs
        }
    }

    if (field.type === "tagPicker") {
        const parsedValue = (value as ITag[]).map((tag) => tag.id)
        const parsedOldValue = (oldValues as ITag[]).map((tag) => tag.id)

        let deltaValue = []
        if (parsedOldValue && parsedOldValue.length) {
            const normalize = (val: string | number): string => val.toString()

            const oldNorm = parsedOldValue.map(normalize)
            const newNorm = parsedValue.map(normalize)

            const oldSet = new Set(oldNorm)
            const newSet = new Set(newNorm)

            const idsToAdd = newNorm.filter((id) => !oldSet.has(id))
            const idsToRemove = oldNorm.filter((id) => !newSet.has(id))

            deltaValue = [
                ...idsToAdd.map((id: string) => ({
                    "@type": "#TF.Tag",
                    id: id,
                })),
                ...idsToRemove.map((id: string) => ({
                    "@removed": {
                        reason: "changed",
                    },
                    "@type": "#TF.Tag",
                    id: id,
                })),
            ]
        } else {
            deltaValue = parsedValue.map((valueId) => ({
                "@type": "#TF.Tag",
                id: valueId,
            }))
        }

        args = {
            "@type": "#TF.TagsItemField",
            "Value@delta": deltaValue,
        } as FieldArgs
    }

    if (field.type === "toggle") {
        const value = field.value as string
        if (value) {
            args.Value = {
                Id: parseInt(value),
            }
        } else {
            args.Value = null
        }
    }

    try {
        const fieldDTO = await dataAPI.patchJSON<FieldDTO>(
            `itemfields(ItemId=${itemId},FieldDefinitionId='${fieldId}')`,
            args,
        )

        if (field.type === "date") {
            fieldDTO.Value = parseOdataDateValue(
                (fieldDTO.Value as string) ?? "",
                getFormatBasedOnType(fieldDTO["@type"]),
            )
        } else if (field.type === "itemPicker") {
            fieldDTO.Value =
                fieldDTO.Value === null
                    ? []
                    : parseItemPickerData(fieldDTO.Value as ItemDTO)
        } else if (field.type === "location") {
            const { MapMarker } = fieldDTO as LocationFieldDTO
            const coordinates = parseLocationCoordinates(
                fieldDTO.Value as LocationFieldDTO["Value"],
            )
            fieldDTO.Value = {
                ...coordinates,
                marker: {
                    id: MapMarker.Id,
                    imageSrc: `${env.applicationPath}/${apiPath}/MapMarkers(${MapMarker.Id})/$value`,
                    anchor: [MapMarker.AnchorX, MapMarker.AnchorY],
                    size: [MapMarker.ImageWidth, MapMarker.ImageHeight],
                },
            }
        } else if (field.type === "time") {
            fieldDTO.Value = parseOdataDateValue(
                (fieldDTO.Value as string) ?? "",
                getFormatBasedOnType(fieldDTO["@type"]),
            )
        } else if (field.type === "toggle") {
            fieldDTO.Value = fieldDTO.Value
                ? parseOptionField(fieldDTO.Value as OptionItemDTO, true)
                : null
        } else if (field.type === "selector") {
            const selectorType = field.odataType as keyof TypeModel

            if (fieldDTO.Value) {
                const mapper = selectorModelMapper(
                    field.odataType as keyof TypeModel,
                )

                const children = []
                if (
                    isFieldTypeSingleSelect(field.odataType as keyof TypeModel)
                ) {
                    const mapped = mapper(
                        fieldDTO.Value as TypeModel[typeof selectorType],
                    )
                    children.push(mapped)
                } else {
                    for (const child of fieldDTO.Value as Array<
                        TypeModel[typeof selectorType]
                    >) {
                        const mapped = mapper(child)
                        children.push(mapped)
                    }
                }
                fieldDTO.Value = children
            }
        } else if (field.type === "tagPicker") {
            fieldDTO.Value =
                fieldDTO.Value === null
                    ? []
                    : (fieldDTO.Value as TagDTO[]).map(parseTagData)
        }

        return {
            ...field,
            value: fieldDTO.Value,
        }
    } catch (error) {
        console.error(
            `An error occurred while updating the field ${field.title} with value ${field.value}: `,
            error,
        )
        const errorMessage = error instanceof Error ? error.message : ""
        if (error instanceof Response) {
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error // Rethrow the error if you need to handle it further up the chain
    }
}

export const collectSelectableValuesGeneric = <
    ApiOptionType extends BasicOptionDTO,
    ResultType,
>(
    itemId: number,
    fieldId: string,
    fieldName: string,
    conversionFunction: (value: ApiOptionType) => ResultType,
    queryOptions?: string,
): Promise<ResultType[]> =>
    dataAPI
        .getJSON<SelectableValuesGeneric<ApiOptionType>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.${fieldName}/SelectableValues${queryOptions}`,
        )
        .then((selectables) => {
            const parsedSelectables = selectables.value
                .filter((selectable) => selectable.IsActive)
                .map((selectable: ApiOptionType) =>
                    conversionFunction(selectable),
                )

            return parsedSelectables
        })

export const collectSelectableValues = (
    itemId: number,
    field: IField,
): Promise<ProductFields.ToggleItem[]> =>
    dataAPI
        .getJSON<SelectableValues>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${field.fieldId}'/TF.OptionItemField/SelectableValues`,
        )
        .then((selectables) => {
            const toggleField = field as IToggleButtonField
            const selectedFieldId = toggleField.value
                ? toggleField.value.valueId
                : "-1"

            const parsedSelectables = selectables.value.map(
                (selectable: OptionItemDTO) =>
                    parseOptionField(
                        selectable,
                        selectedFieldId === selectable.Id.toString(),
                    ),
            )

            return parsedSelectables
        })

export const collectStateTransitionButtons = (
    itemId: number,
): Promise<FlowControlDTO[]> =>
    collectSelectableValuesGeneric(
        itemId,
        StatusCode,
        "StatusItemField",
        (value) => value as FlowControlDTO,
        "",
    )
        .catch((reason) => {
            if (reason.status === 404) {
                return [] as FlowControlDTO[]
            } else {
                throw reason
            }
        })
        .then((buttons) =>
            buttons.filter(
                // Only a FlowControl will have a "Type" property,
                // this will filter out normal statuses and hidden flow controls
                (button) => !!button.Type && button.Type !== "Hidden",
            ),
        )

export const collectItemActions = (itemId: number): Promise<IButton[]> =>
    dataAPI
        .getJSON<ODataGetArrayResponse<ItemActionDTO>>(
            `ItemViews(ItemId=${itemId},Code='condensed')/Actions?$filter=Action/ApplicationPart has TF.ApplicationPart'Web'&$orderby=SequenceNr`,
        )
        .then((itemActions) => {
            return itemActions.value.map((itemAction) =>
                parseItemAction(itemAction),
            )
        })

const extractComponents = (
    value: ItemViewComponentDTO | ItemViewGroupingComponentDTO,
): (ItemViewComponentDTO | ItemViewGroupingComponentDTO)[] => {
    const array = []
    if (value) {
        array.push(value)
    }

    if ("Components" in value) {
        array.push(
            ...value.Components.flatMap((component) =>
                extractComponents(component),
            ),
        )
    }
    return array
}

export const extractFields = (
    itemDetail: ItemViewGroupingComponentDTO,
    userFormattingSettings: IUserFormattingSettings,
): IField[] => {
    const components = extractComponents(itemDetail)

    // Currently, sections aren't shown in the base NewUI.
    // Therefore, the description label is not shown, as it's only on the section component.
    // To fix that, we copy the label of the field into the actual field (where it's usually empty).
    const descriptionComponents = components.filter(
        (component) => component.ComponentCode === DescriptionComponentCode,
    )
    const sectionComponent = descriptionComponents?.[0] ?? null
    const textComponent = descriptionComponents?.[1] ?? null
    if (
        textComponent?.ComponentDefinition &&
        sectionComponent?.ComponentDefinition
    ) {
        textComponent.ComponentDefinition.Label =
            sectionComponent.ComponentDefinition.Label
    }

    // We add the concept field as it's needed in other places.
    const conceptItemField =
        itemDetail.MetaData.filter(
            (item) => item.ComponentCode === ConceptCode,
        )[0] ?? null
    if (conceptItemField) {
        components.push(conceptItemField)
    }

    return components.map((field) =>
        parseFieldData(
            field as
                | SingleFieldComponentDTO
                | MultiFieldsComponentDTO
                | ItemViewGroupingComponentDTO,
            userFormattingSettings,
        ),
    )
}

export const parseTabs = (
    itemDetail: ItemViewGroupingComponentDTO,
    userFormattingSettings: IUserFormattingSettings,
): ITabComponent[] => {
    const description = itemDetail.MetaData.filter(
        (component) => component.ComponentCode === DescriptionComponentCode,
    ) as ItemViewComponentDTO[]
    const tabs = itemDetail.Components.map(
        (viewComponent: ItemViewGroupingComponentDTO, index) => {
            const components = viewComponent.Components
            if (index == 0) {
                components.concat(description)
            }

            return {
                label: viewComponent.ComponentDefinition.Label,
                code: viewComponent.ComponentCode,
                fields: components.map((field) =>
                    parseFieldData(
                        field as
                            | SingleFieldComponentDTO
                            | MultiFieldsComponentDTO,
                        userFormattingSettings,
                    ),
                ),
            } as ITabComponent
        },
    )

    return tabs
}
